import { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend,
} from 'chart.js';
import {
  fieldWorks as api,
  type FieldWork, type FieldWorkFilter, type FieldWorkKindCode,
  type FieldWorkMeta, type FieldWorkStatusCode,
} from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { PraceGantt, odmianaPrac } from '@/components/PraceGantt';

// react-chartjs-2 rejestruje SAME kontrolery — bez tego skale i elementy wysypią się dopiero
// w przeglądarce, a TypeScript tego nie złapie. Rejestracja w zasięgu modułu, nie w komponencie.
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const DAY_MS = 86_400_000;

/** ISO → lokalna północ. `new Date('2026-08-25')` to UTC i w PL cofa datę o dzień. */
function parseIso(iso: string): Date {
  return new Date(iso + 'T00:00:00');
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MONTHS = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${String(y).slice(2)}`;
}

/**
 * Harmonogram i wykresy prac terenowych.
 *
 * Wszystko liczone jest z TEJ SAMEJ odpowiedzi `/api/field-works` co rejestr — bez osobnych
 * endpointów agregujących. Dzięki temu wykres nie może pokazać czegoś innego niż tabela obok,
 * a filtry działają na jednym i drugim. `FieldWorkDto` niesie już wszystko, czego trzeba:
 * terminy, status, termin umowy, kwotę.
 */
export function PraceHarmonogramPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<FieldWork[] | null>(null);
  const [meta, setMeta] = useState<FieldWorkMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [statuses, setStatuses] = useState<FieldWorkStatusCode[]>([]);
  const [kind, setKind] = useState<FieldWorkKindCode | ''>('');
  const [executedFrom, setExecutedFrom] = useState('');
  const [executedTo, setExecutedTo] = useState('');

  const filter: FieldWorkFilter = useMemo(() => ({
    status: statuses.length ? statuses : undefined,
    kind: kind ? [kind] : undefined,
    executedFrom: executedFrom || undefined,
    executedTo: executedTo || undefined,
  }), [statuses, kind, executedFrom, executedTo]);

  useEffect(() => {
    setErr(null);
    api.list(filter).then(setRows).catch(e => setErr(e?.message ?? String(e)));
  }, [filter]);

  useEffect(() => { api.meta().then(setMeta).catch(() => {}); }, []);

  const toggleStatus = (s: FieldWorkStatusCode) =>
    setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  // ── Obłożenie w czasie ────────────────────────────────────────────────────
  // Ile prac było w robocie w danym miesiącu. Praca liczy się do KAŻDEGO miesiąca, na który
  // zachodzi którykolwiek jej termin — bo tyle miesięcy realnie zajmowała brygadę.
  const occupancy = useMemo(() => {
    if (!rows) return null;
    const perMonth = new Map<string, Set<number>>();
    for (const w of rows) {
      for (const d of w.dates) {
        if (!d.dateFrom) continue;
        const from = parseIso(d.dateFrom);
        const to = parseIso(d.dateTo ?? d.dateFrom);
        const cur = new Date(from.getFullYear(), from.getMonth(), 1);
        const last = new Date(to.getFullYear(), to.getMonth(), 1);
        while (cur <= last) {
          const k = monthKey(cur);
          if (!perMonth.has(k)) perMonth.set(k, new Set());
          perMonth.get(k)!.add(w.id);
          cur.setMonth(cur.getMonth() + 1);
        }
      }
    }
    const keys = [...perMonth.keys()].sort();
    if (keys.length === 0) return null;
    // Oś MUSI być ciągła: pominięcie miesiąca bez prac rysowałoby „mar | maj" obok siebie
    // i miesiąc całkowitego przestoju brygady byłby nie do zauważenia.
    const [y0, m0] = keys[0].split('-').map(Number);
    const [y1, m1] = keys[keys.length - 1].split('-').map(Number);
    const pelne: string[] = [];
    for (let y = y0, m = m0; y < y1 || (y === y1 && m <= m1); m === 12 ? (m = 1, y++) : m++) {
      pelne.push(`${y}-${String(m).padStart(2, '0')}`);
    }
    return {
      labels: pelne.map(monthLabel),
      counts: pelne.map(k => perMonth.get(k)?.size ?? 0),
    };
  }, [rows]);

  // ── Terminowość ───────────────────────────────────────────────────────────
  // Liczona SAMODZIELNIE, bo serwerowe `overdue` dotyczy wyłącznie prac otwartych — a tutaj
  // pytamy o historię: czy zamknięte roboty zmieściły się w terminie umowy.
  const punctuality = useMemo(() => {
    if (!rows) return null;
    let onTime = 0, late = 0, noDeadline = 0, openOverdue = 0;
    const delays: number[] = [];
    for (const w of rows) {
      if (w.status !== 'ZAKONCZONE') {
        if (w.daysToDeadline != null && w.daysToDeadline < 0) openOverdue++;
        continue;
      }
      if (!w.contractDeadline) { noDeadline++; continue; }
      if (!w.lastDate) { noDeadline++; continue; }
      const diff = Math.round(
        (parseIso(w.lastDate).getTime() - parseIso(w.contractDeadline).getTime()) / DAY_MS);
      if (diff <= 0) onTime++;
      else { late++; delays.push(diff); }
    }
    // Mediana parzystej liczby próbek to ŚREDNIA dwóch środkowych, nie górna z nich —
    // przy dwóch opóźnieniach (1 i 9 dni) wersja uproszczona pokazywała „9", czyli maksimum.
    const median = (() => {
      if (delays.length === 0) return null;
      const p = [...delays].sort((a, b) => a - b);
      const mid = Math.floor(p.length / 2);
      return p.length % 2 ? p[mid] : Math.round((p[mid - 1] + p[mid]) / 2);
    })();
    return { onTime, late, noDeadline, openOverdue, worstDelay: delays.length ? Math.max(...delays) : null, median };
  }, [rows]);

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  } as const;

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1>Harmonogram prac</h1>
          <div className="text-ink-400 text-[13px]">
            Oś czasu, obłożenie brygad i terminowość — liczone z tych samych danych co rejestr
          </div>
        </div>
        <div className="flex gap-2">
          <NavLink to="/prace" className="btn btn-secondary btn-sm">
            <Icon name="layout" className="w-3.5 h-3.5"/> Rejestr prac
          </NavLink>
          <NavLink to="/mapa-przeglad" className="btn btn-secondary btn-sm">
            <Icon name="map" className="w-3.5 h-3.5"/> Mapa
          </NavLink>
        </div>
      </header>

      {err && <div className="text-red-600 p-3 bg-red-50 border border-red-200 rounded mb-3">{err}</div>}

      <div className="card mb-4">
        <div className="card-head flex-wrap gap-2">
          <div className="flex gap-1">
            {(meta?.statuses ?? []).map(s => (
              <button key={s.id} onClick={() => toggleStatus(s.id)}
                      className={'btn btn-sm ' + (statuses.includes(s.id) ? 'btn-primary' : 'btn-ghost')}>
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: s.mapColor }}/>
                {s.name}
              </button>
            ))}
          </div>
          <div className="w-[190px]">
            <SearchableSelect value={kind} onChange={v => setKind(v as FieldWorkKindCode | '')}>
              <option value="">Rodzaj — wszystkie</option>
              {(meta?.kinds ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </SearchableSelect>
          </div>
          <label className="flex items-center gap-1.5 text-[12px]">
            <span className="text-ink-500">Wykonane od</span>
            <input type="date" value={executedFrom} onChange={e => setExecutedFrom(e.target.value)}
                   className="input !py-1 !text-[12px] w-[135px]"/>
          </label>
          <label className="flex items-center gap-1.5 text-[12px]">
            <span className="text-ink-500">do</span>
            <input type="date" value={executedTo} onChange={e => setExecutedTo(e.target.value)}
                   className="input !py-1 !text-[12px] w-[135px]"/>
          </label>
          <span className="ml-auto text-[12px] text-ink-400">
            {rows?.length ?? 0} {odmianaPrac(rows?.length ?? 0)}
          </span>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-head"><div className="card-title">Oś czasu (Gantt)</div></div>
        <div className="card-body !p-0">
          {!rows ? <div className="p-8 text-center text-ink-400">Ładowanie…</div>
            : <PraceGantt works={rows} meta={meta} onSelect={id => nav(`/prace/${id}`)}/>}
        </div>
      </div>

      {/* Poniżej ~1100 px dwa canvasy obok siebie robią się węższe niż ich własne osie —
          wtedy układ przechodzi na jedną kolumnę. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-head">
            <div className="card-title">Obłożenie w czasie</div>
            <span className="ml-auto text-[11px] text-ink-400">prac równolegle w miesiącu</span>
          </div>
          <div className="card-body">
            {!occupancy ? (
              <div className="p-6 text-center text-ink-400 text-[13px]">
                Brak prac z rozpoznanymi terminami — nie ma z czego policzyć obłożenia.
              </div>
            ) : (
              // Wysokość rodzica w px jest WYMAGANA przy maintainAspectRatio:false —
              // inaczej canvas rośnie w nieskończoność przy każdym przeliczeniu układu.
              <div style={{ height: 260 }}>
                <Bar options={chartOpts} data={{
                  labels: occupancy.labels,
                  datasets: [{
                    label: 'Prace',
                    data: occupancy.counts,
                    backgroundColor: '#0891b2',
                    borderRadius: 3,
                  }],
                }}/>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">Terminowość</div>
            <span className="ml-auto text-[11px] text-ink-400">prace zakończone vs termin umowy</span>
          </div>
          <div className="card-body">
            {!punctuality ? <div className="p-6 text-center text-ink-400">Ładowanie…</div> : (
              <>
                <div style={{ height: 200 }}>
                  <Bar options={chartOpts} data={{
                    labels: ['W terminie', 'Po terminie', 'Bez terminu w umowie'],
                    datasets: [{
                      label: 'Prace',
                      data: [punctuality.onTime, punctuality.late, punctuality.noDeadline],
                      backgroundColor: ['#16a34a', '#dc2626', '#9ca3af'],
                      borderRadius: 3,
                    }],
                  }}/>
                </div>
                <div className="text-[12px] text-ink-600 grid gap-1 mt-3 pt-3 border-t border-ink-100">
                  {punctuality.late > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span>Mediana opóźnienia:</span>
                        <strong>{punctuality.median} dni</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Największe opóźnienie:</span>
                        <strong className="text-red-600">{punctuality.worstDelay} dni</strong>
                      </div>
                    </>
                  )}
                  {punctuality.openOverdue > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Otwarte prace już po terminie:</span>
                      <strong>{punctuality.openOverdue}</strong>
                    </div>
                  )}
                  {punctuality.noDeadline > 0 && (
                    <div className="text-[11px] text-ink-400 mt-1">
                      „Bez terminu w umowie" to prace, których nie da się ocenić — brak daty
                      w umowie albo brak wpisanego terminu wykonania.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
